import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyIcon, PencilSquareIcon, PlusIcon, SignalIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  DescriptionList,
  EmptyState,
  EnvironmentBadge,
  Modal,
  PageHeader,
  SelectField,
  SkeletonRows,
  TextAreaField,
  TextField,
  useToast,
  type Column,
} from '@csa/ui';
import { ENVIRONMENTS, HTTP_METHODS, TARGET_KINDS, createTargetSchema } from '@csa/shared';
import { api, ApiError } from '../../lib/api.js';
import { useTimeZone } from '../../lib/TimeZoneProvider.js';

/**
 * Targets and secret bindings.
 *
 * The most consequential configuration surface in the tool: a target row
 * decides which database a statement reaches, which egress ranges an API
 * call may resolve to, and which service account it runs as.
 *
 * Two things this page deliberately cannot do.
 *
 * It cannot show a secret value. Binding stores an ARN; the detail panel
 * shows the ARN plus rotation state read from Secrets Manager metadata, and
 * there is no endpoint that returns `SecretString`. A person with full
 * administrative rights in this tool still cannot read a database password,
 * and that is one of the things that makes the service accounts defensible.
 *
 * It cannot delete a target. Deleting one would cascade to its bindings and
 * role grants and leave every historical job pointing at an id that resolves
 * to nothing -- so "which database did this run against?" would have no
 * answer, which is the one question an audit trail exists to answer.
 * Disabling stops all new work immediately and keeps the history readable.
 */

export interface AdminTarget {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly kind: string;
  readonly environment: string;
  readonly enabled: boolean;
  readonly config: Record<string, unknown>;
  readonly max_concurrency: number;
  readonly statement_timeout_ms: number;
  readonly interactive_row_cap: number;
  readonly batch_row_cap: number;
  readonly allow_scripts: boolean;
  readonly proc_allowlist: readonly string[];
  readonly schema_allowlist: readonly string[];
  readonly database_allowlist: readonly string[];
  readonly granted_roles: readonly string[];
  readonly binding_count: number;
  readonly updated_at: string;
}

interface Binding {
  readonly id: string;
  readonly role: string;
  readonly secretArn: string;
  readonly description: string;
  readonly createdAt: string;
  readonly createdByUpn: string | null;
  readonly secret: {
    readonly name: string;
    readonly description: string | null;
    readonly rotationEnabled: boolean;
    readonly rotationIntervalDays: number | null;
    readonly lastRotatedAt: string | null;
    readonly deletedAt: string | null;
  } | null;
}

interface BreakerStatus {
  readonly state: 'closed' | 'open' | 'half-open';
  readonly consecutiveFailures: number;
  readonly lastFailureCode: string | null;
  readonly lastFailureAt: string | null;
  readonly openedAt: string | null;
}

export function TargetsPage(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<AdminTarget | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);

  const targets = useQuery({
    queryKey: ['admin-targets'],
    queryFn: () => api.get<{ targets: AdminTarget[] }>('/api/admin/targets'),
  });

  const save = useMutation({
    mutationFn: (draft: Draft) =>
      draft.id
        ? api.patch(`/api/admin/targets/${draft.id}`, draftToBody(draft))
        : api.post('/api/admin/targets', draftToBody(draft)),
    onSuccess: (_result, draft) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-targets'] });
      setEditing(null);
      /**
       * The detail panel holds a row snapshot taken before the edit, so it
       * is closed rather than left showing values that are no longer what
       * the target will execute against.
       */
      setSelected(null);
      toast(
        draft.id
          ? { tone: 'success', title: 'Target updated' }
          : {
              tone: 'success',
              title: 'Target registered',
              message: 'Bind a credential to it before it can execute anything.',
            },
      );
    },
    onError: (error: unknown) =>
      toast({
        tone: 'danger',
        title: 'Could not save the target',
        message: error instanceof ApiError ? error.message : String(error),
      }),
  });

  const columns: Column<AdminTarget>[] = [
    {
      key: 'key',
      header: 'Target',
      render: (target) => (
        <div className="flex flex-col">
          <span className={target.enabled ? '' : 'line-through opacity-60'}>{target.name}</span>
          <code className="font-mono text-xs text-subtle">{target.key}</code>
        </div>
      ),
    },
    { key: 'kind', header: 'Kind', render: (t) => <Badge tone="neutral">{t.kind}</Badge> },
    {
      key: 'environment',
      header: 'Environment',
      render: (t) => <EnvironmentBadge environment={t.environment} />,
    },
    {
      key: 'address',
      header: 'Points at',
      render: (target) => <code className="font-mono text-xs">{describeAddress(target)}</code>,
    },
    {
      key: 'binding_count',
      header: 'Credentials',
      render: (target) =>
        target.binding_count === 0 ? (
          // A target with no credential cannot run anything, so it is a
          // configuration that is silently inert. Flagged rather than shown
          // as a zero.
          <Badge tone="warning">none bound</Badge>
        ) : (
          <span className="csa-numeric text-xs">{target.binding_count}</span>
        ),
    },
    {
      key: 'granted_roles',
      header: 'Roles',
      render: (target) =>
        target.granted_roles.length === 0 ? (
          <span className="text-subtle">—</span>
        ) : (
          <span className="text-xs">{target.granted_roles.join(', ')}</span>
        ),
    },
    {
      key: 'enabled',
      header: 'Status',
      render: (t) => (t.enabled ? <Badge tone="success">enabled</Badge> : <Badge>disabled</Badge>),
    },
  ];

  if (targets.isPending) return <SkeletonRows rows={6} />;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PageHeader
        title="Targets & secrets"
        description="Databases and API endpoints this tool may reach, and the managed credentials bound to them."
        actions={
          <Button
            leadingIcon={<PlusIcon className="size-4" aria-hidden />}
            onClick={() => setEditing({ ...BLANK_DRAFT })}
          >
            Register target
          </Button>
        }
      />

      {(targets.data?.targets.length ?? 0) === 0 ? (
        <EmptyState
          title="No targets are registered"
          description="Until a target exists, every target-scoped permission confers nothing and the consoles have nothing to offer."
          action={
            <Button
              leadingIcon={<PlusIcon className="size-4" aria-hidden />}
              onClick={() => setEditing({ ...BLANK_DRAFT })}
            >
              Register the first target
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={targets.data!.targets}
          rowKey={(target) => target.id}
          onRowClick={setSelected}
          caption="Registered connection targets"
        />
      )}

      {selected ? (
        <TargetDetail target={selected} onEdit={() => setEditing(targetToDraft(selected))} />
      ) : null}

      {editing ? (
        <TargetEditor
          draft={editing}
          onChange={setEditing}
          onSave={() => save.mutate(editing)}
          onClose={() => setEditing(null)}
          pending={save.isPending}
        />
      ) : null}
    </div>
  );
}

function TargetDetail({
  target,
  onEdit,
}: {
  target: AdminTarget;
  onEdit: () => void;
}): ReactElement {
  const { toast } = useToast();
  const tz = useTimeZone();
  const queryClient = useQueryClient();

  const bindings = useQuery({
    queryKey: ['target-secrets', target.id],
    queryFn: () => api.get<{ bindings: Binding[] }>(`/api/admin/targets/${target.id}/secrets`),
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<{ reachable: boolean; durationMs: number; message: string | null }>(
        `/api/admin/targets/${target.id}/test`,
      ),
    onSuccess: (result) =>
      toast(
        result.reachable
          ? {
              tone: 'success',
              title: 'Reachable',
              message: `Connected on the read credential in ${result.durationMs} ms.`,
            }
          : {
              tone: 'danger',
              title: 'Could not connect',
              // The driver error is shown in full. It is a connection error
              // from a service account against a configured host; redacting
              // it would leave an administrator guessing between a wrong
              // password and a closed security group.
              message: result.message ?? 'No further detail.',
              durationMs: 0,
            },
      ),
    onError: (error: unknown) =>
      toast({
        tone: 'danger',
        title: 'Test failed',
        message: error instanceof ApiError ? error.message : String(error),
      }),
  });

  const breaker = useQuery({
    queryKey: ['target-breaker', target.id],
    queryFn: () => api.get<BreakerStatus>(`/api/admin/targets/${target.id}/breaker`),
  });

  const reopen = useMutation({
    mutationFn: () => api.post(`/api/admin/targets/${target.id}/breaker/reset`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['target-breaker', target.id] });
      toast({
        tone: 'success',
        title: 'Breaker closed',
        message: 'Work will be sent to this target again. It reopens if the failures continue.',
      });
    },
    onError: (error: unknown) =>
      toast({
        tone: 'danger',
        title: 'Could not close the breaker',
        message: error instanceof ApiError ? error.message : String(error),
      }),
  });

  const bind = useMutation({
    mutationFn: (input: { role: string; secretArn: string; description: string }) =>
      api.put(`/api/admin/targets/${target.id}/secrets`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['target-secrets', target.id] });
      void queryClient.invalidateQueries({ queryKey: ['admin-targets'] });
      toast({
        tone: 'success',
        title: 'Credential bound',
        message: 'The worker resolves it at execution. It is never sent to a browser.',
      });
    },
    onError: (error: unknown) =>
      toast({
        tone: 'danger',
        title: 'Could not bind',
        message: error instanceof ApiError ? error.message : String(error),
      }),
  });

  return (
    <Card className="flex flex-col gap-4 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium">{target.name}</h2>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<PencilSquareIcon className="size-4" aria-hidden />}
            onClick={onEdit}
          >
            Edit
          </Button>
          {target.kind !== 'rest' ? (
            <Button
              size="sm"
              variant="secondary"
              loading={test.isPending}
              leadingIcon={<SignalIcon className="size-4" aria-hidden />}
              onClick={() => test.mutate()}
            >
              Test connection
            </Button>
          ) : null}
        </div>
      </div>

      {breaker.data && breaker.data.state !== 'closed' ? (
        <Alert tone="warning">
          <p className="mb-1 font-medium">
            {breaker.data.state === 'open'
              ? 'The circuit breaker is open — work against this target is being refused'
              : 'The circuit breaker is testing whether this target has recovered'}
          </p>
          <p className="mb-2">
            {breaker.data.consecutiveFailures} consecutive connection failures
            {breaker.data.lastFailureCode ? `, last one ${breaker.data.lastFailureCode}` : ''}
            {breaker.data.lastFailureAt ? (
              <>
                {' at '}
                <span title={tz.titleFor(breaker.data.lastFailureAt)}>
                  {tz.format(breaker.data.lastFailureAt)}
                </span>
              </>
            ) : null}
            . It closes on its own once a probe succeeds; close it now only if you have fixed the
            cause.
          </p>
          <Button
            size="sm"
            variant="secondary"
            loading={reopen.isPending}
            onClick={() => reopen.mutate()}
          >
            Close the breaker
          </Button>
        </Alert>
      ) : null}

      <DescriptionList
        items={[
          { label: 'Key', value: <code className="font-mono text-xs">{target.key}</code> },
          { label: 'Description', value: target.description || '—' },
          {
            label: 'Points at',
            value: <code className="font-mono text-xs">{describeAddress(target)}</code>,
          },
          {
            label: 'Statement timeout',
            value: `${(target.statement_timeout_ms / 1000).toFixed(0)}s`,
          },
          {
            label: 'Row caps',
            value: `${target.interactive_row_cap.toLocaleString()} interactive / ${target.batch_row_cap.toLocaleString()} batch`,
          },
          {
            label: 'Concurrency',
            value: `${target.max_concurrency} simultaneous executions`,
          },
          {
            label: 'Schemas allowlisted',
            value:
              target.schema_allowlist.length === 0 ? (
                // An empty allowlist is the absence of a further narrowing,
                // not the absence of permission -- the account is already a
                // reader on an approved database. Spelling that out stops
                // somebody reading a blank as "locked down".
                <span className="text-muted">none — everything the read account can see</span>
              ) : (
                target.schema_allowlist.join(', ')
              ),
          },
          {
            label: 'Procedures allowlisted',
            value:
              target.proc_allowlist.length === 0 ? (
                <span className="text-muted">none — no procedure may be called</span>
              ) : (
                target.proc_allowlist.join(', ')
              ),
          },
          /**
           * Only on the kind it means something for. Shown as a row on
           * every target it would read as a control that exists everywhere
           * and happens to be empty here, which is the opposite of true:
           * on a single-database target there is nothing to allowlist.
           */
          ...(target.kind === 'mssql_server'
            ? [
                {
                  label: 'Databases allowlisted',
                  value:
                    target.database_allowlist.length === 0 ? (
                      <span className="text-warning">
                        none — every database the read account can open
                      </span>
                    ) : (
                      target.database_allowlist.join(', ')
                    ),
                },
              ]
            : []),
          { label: 'Roles granted', value: target.granted_roles.join(', ') || '—' },
        ]}
      />

      {target.kind === 'rest' ? <RestConfig config={target.config} /> : null}

      <section>
        <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-subtle">
          <KeyIcon className="size-4" aria-hidden />
          Managed credentials
        </h3>

        {bindings.isPending ? (
          <SkeletonRows rows={2} />
        ) : (bindings.data?.bindings.length ?? 0) === 0 ? (
          <Alert tone="warning">
            Nothing is bound, so this target cannot execute anything. Bind at least the read
            credential below.
          </Alert>
        ) : (
          <ul className="flex flex-col gap-2">
            {bindings.data!.bindings.map((binding) => (
              <li key={binding.id} className="rounded-md border border-line p-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={binding.role === 'write' ? 'warning' : 'neutral'}>
                    {binding.role}
                  </Badge>
                  <span>{binding.description || binding.secret?.name || 'unnamed'}</span>

                  {binding.secret === null ? (
                    // The secret could not be described. That is exactly the
                    // state an administrator needs to see, and it is why the
                    // list tolerates the failure instead of failing to load.
                    <Badge tone="danger">unreachable in Secrets Manager</Badge>
                  ) : binding.secret.deletedAt ? (
                    <Badge tone="danger">pending deletion</Badge>
                  ) : binding.secret.rotationEnabled ? (
                    <Badge tone="success">
                      rotates every {binding.secret.rotationIntervalDays ?? '?'} days
                    </Badge>
                  ) : (
                    <Badge tone="warning">rotation off</Badge>
                  )}
                </div>

                <p className="mt-1 break-all font-mono text-[0.7rem] text-subtle">
                  {binding.secretArn}
                </p>

                <p className="mt-1 text-xs text-muted">
                  Bound{' '}
                  <span title={tz.titleFor(binding.createdAt)}>{tz.format(binding.createdAt)}</span>
                  {binding.createdByUpn ? ` by ${binding.createdByUpn}` : ''}
                  {binding.secret?.lastRotatedAt ? (
                    <>
                      {' · last rotated '}
                      <span title={tz.titleFor(binding.secret.lastRotatedAt)}>
                        {tz.format(binding.secret.lastRotatedAt)}
                      </span>
                    </>
                  ) : (
                    ' · never rotated'
                  )}
                </p>
              </li>
            ))}
          </ul>
        )}

        <BindForm onSubmit={(input) => bind.mutate(input)} pending={bind.isPending} />
      </section>
    </Card>
  );
}

function RestConfig({ config }: { config: Record<string, unknown> }): ReactElement {
  const egress = asStrings(config.allowedEgressCidrs);

  return (
    <section>
      <h3 className="mb-1.5 text-xs font-medium text-subtle">Endpoint configuration</h3>
      <DescriptionList
        items={[
          {
            label: 'Base URL',
            value: <code className="font-mono text-xs">{String(config.baseUrl ?? '')}</code>,
          },
          { label: 'Methods', value: asStrings(config.allowedMethods).join(', ') || '—' },
          {
            label: 'Path allowlist',
            value: (
              <div className="flex flex-wrap gap-1">
                {asStrings(config.pathAllowlist).map((path) => (
                  <code key={path} className="rounded bg-sunken px-1.5 py-0.5 font-mono text-xs">
                    {path}
                  </code>
                ))}
              </div>
            ),
          },
          {
            label: 'Egress ranges',
            value:
              egress.length === 0 ? (
                // The safe default, stated as such. An empty list means
                // public addresses only; a private range here is how an
                // internal endpoint becomes reachable, which is the change
                // an SSRF review starts from.
                <span className="text-muted">none — public addresses only</span>
              ) : (
                <span className="text-warning-fg">{egress.join(', ')}</span>
              ),
          },
          {
            label: 'Rate limit',
            value: `${String(config.rateLimitPerSecond ?? '?')}/s, burst ${String(config.rateLimitBurst ?? '?')}`,
          },
        ]}
      />
    </section>
  );
}

function BindForm({
  onSubmit,
  pending,
}: {
  onSubmit: (input: { role: string; secretArn: string; description: string }) => void;
  pending: boolean;
}): ReactElement {
  const [role, setRole] = useState('read');
  const [secretArn, setSecretArn] = useState('');
  const [description, setDescription] = useState('');

  const looksLikeArn = /^arn:aws[a-z-]*:secretsmanager:[a-z0-9-]+:\d{12}:secret:.+$/.test(
    secretArn.trim(),
  );

  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-line-strong p-2">
      <SelectField
        label="Role"
        containerClassName="w-32"
        value={role}
        options={[
          { value: 'read', label: 'read' },
          { value: 'write', label: 'write' },
        ]}
        onChange={(event) => setRole(event.target.value)}
      />

      <TextField
        label="Secret ARN"
        containerClassName="min-w-80 flex-1"
        className="font-mono text-xs"
        placeholder="arn:aws:secretsmanager:ap-southeast-2:123456789012:secret:csa/billing-prod/read-AbCdEf"
        hint="Only the ARN is stored. The value is resolved by the worker and never reaches a browser."
        error={
          secretArn.trim().length > 0 && !looksLikeArn ? 'That is not a secret ARN.' : undefined
        }
        value={secretArn}
        onChange={(event) => setSecretArn(event.target.value)}
      />

      <TextField
        label="Label"
        containerClassName="w-56"
        placeholder="Billing read account"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />

      <Button
        loading={pending}
        disabled={!looksLikeArn}
        onClick={() => {
          onSubmit({ role, secretArn: secretArn.trim(), description });
          setSecretArn('');
          setDescription('');
        }}
      >
        Bind
      </Button>
    </div>
  );
}

/**
 * The registration and edit form.
 *
 * Flat, rather than mirroring the nested `config` union, because the shape
 * an operator thinks in is "which host, which database, how large may a
 * result be" and not a discriminated union. `draftToBody` assembles the
 * union at the boundary.
 *
 * The draft is validated with the server's own `createTargetSchema`, so the
 * form cannot accept something the API will reject, and cannot refuse
 * something the API would have taken. A second, hand-written copy of these
 * rules would drift, and the direction it drifts in is the dangerous one --
 * a client that quietly permits a little more than the server intended.
 */
export interface Draft {
  /** Absent when registering. Its presence is what selects PATCH over POST. */
  readonly id?: string;
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly kind: string;
  readonly environment: string;
  readonly enabled: boolean;
  readonly maxConcurrency: number;
  /**
   * Seconds here, milliseconds on the wire. Nobody reasons about a query
   * budget in milliseconds, and a mistyped trailing zero costs a factor of
   * ten either way.
   */
  readonly statementTimeoutSeconds: number;
  readonly interactiveRowCap: number;
  readonly batchRowCap: number;
  readonly allowScripts: boolean;
  readonly procAllowlist: string;
  readonly schemaAllowlist: string;
  readonly databaseAllowlist: string;
  readonly host: string;
  readonly port: number;
  readonly database: string;
  readonly encrypt: boolean;
  readonly trustServerCertificate: boolean;
  readonly baseUrl: string;
  readonly pathAllowlist: string;
  readonly allowedMethods: readonly string[];
  readonly allowedEgressCidrs: string;
  readonly rateLimitPerSecond: number;
  readonly rateLimitBurst: number;
}

/** Conventional listener ports, so the field starts usable rather than at zero. */
const DEFAULT_PORTS: Record<string, number> = { mssql: 1433, oracle: 1521, mssql_server: 1433 };

/**
 * What each kind is, in a sentence, next to the picker.
 *
 * `mssql` and `mssql_server` differ in one word and in how much they reach,
 * which is not a distinction to leave to an operator inferring it from the
 * name. The server kind is the broader grant and it says so.
 */
const KIND_DESCRIPTIONS: Record<string, string> = {
  mssql: 'One SQL Server database. Queries cannot leave it.',
  mssql_server: 'A whole SQL Server instance — every database the service account can open.',
  oracle: 'One Oracle service.',
  rest: 'An HTTPS endpoint, called with a managed secret.',
};

export const BLANK_DRAFT: Draft = {
  key: '',
  name: '',
  description: '',
  kind: 'mssql',
  environment: 'prod',
  enabled: true,
  maxConcurrency: 4,
  statementTimeoutSeconds: 60,
  interactiveRowCap: 10_000,
  batchRowCap: 5_000_000,
  allowScripts: false,
  procAllowlist: '',
  schemaAllowlist: '',
  databaseAllowlist: '',
  host: '',
  port: 1433,
  database: '',
  encrypt: true,
  trustServerCertificate: false,
  baseUrl: '',
  pathAllowlist: '',
  allowedMethods: ['GET'],
  allowedEgressCidrs: '',
  rateLimitPerSecond: 10,
  rateLimitBurst: 20,
};

/** One entry per line, blanks dropped. */
function lines(value: string): string[] {
  return value
    .split('\n')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

export function draftToBody(draft: Draft): Record<string, unknown> {
  const common = {
    key: draft.key.trim(),
    name: draft.name.trim(),
    description: draft.description.trim(),
    kind: draft.kind,
    environment: draft.environment,
    enabled: draft.enabled,
    maxConcurrency: draft.maxConcurrency,
    statementTimeoutMs: draft.statementTimeoutSeconds * 1000,
    interactiveRowCap: draft.interactiveRowCap,
    batchRowCap: draft.batchRowCap,
    // Never for a REST target: the flag is about SQL statements, and
    // sending it on an API target would record a control that does not
    // apply as though an administrator had considered it.
    allowScripts: draft.kind === 'rest' ? false : draft.allowScripts,
    procAllowlist: lines(draft.procAllowlist),
    schemaAllowlist: lines(draft.schemaAllowlist),
    /**
     * Only ever sent on the kind it applies to, for the same reason as
     * `allowScripts` above. A database allowlist stored against a target
     * that has one database would read, in an access review, as a narrowing
     * somebody chose -- when in fact nothing would consult it.
     */
    databaseAllowlist: draft.kind === 'mssql_server' ? lines(draft.databaseAllowlist) : [],
  };

  if (draft.kind === 'rest') {
    return {
      ...common,
      config: {
        baseUrl: draft.baseUrl.trim(),
        pathAllowlist: lines(draft.pathAllowlist),
        allowedMethods: draft.allowedMethods,
        defaultHeaders: {},
        allowedEgressCidrs: lines(draft.allowedEgressCidrs),
        rateLimitPerSecond: draft.rateLimitPerSecond,
        rateLimitBurst: draft.rateLimitBurst,
      },
    };
  }

  /**
   * No `database` on a server-scoped target, and the server rejects one.
   *
   * It is not merely unnecessary: a database sitting in the config of a
   * target whose submissions each choose their own would be a value that
   * looks like a constraint and is not one. Every statement would run
   * wherever its submission said, while an access review read the config
   * and concluded the target reached one database.
   */
  if (draft.kind === 'mssql_server') {
    return {
      ...common,
      config: {
        host: draft.host.trim(),
        port: draft.port,
        encrypt: draft.encrypt,
        trustServerCertificate: draft.trustServerCertificate,
      },
    };
  }

  return {
    ...common,
    config: {
      host: draft.host.trim(),
      port: draft.port,
      database: draft.database.trim(),
      encrypt: draft.encrypt,
      trustServerCertificate: draft.trustServerCertificate,
    },
  };
}

export function targetToDraft(target: AdminTarget): Draft {
  const config = target.config;
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const num = (value: unknown, fallback: number): number =>
    typeof value === 'number' ? value : fallback;

  return {
    id: target.id,
    key: target.key,
    name: target.name,
    description: target.description,
    kind: target.kind,
    environment: target.environment,
    enabled: target.enabled,
    maxConcurrency: target.max_concurrency,
    statementTimeoutSeconds: Math.round(target.statement_timeout_ms / 1000),
    interactiveRowCap: target.interactive_row_cap,
    batchRowCap: target.batch_row_cap,
    allowScripts: target.allow_scripts,
    procAllowlist: target.proc_allowlist.join('\n'),
    schemaAllowlist: target.schema_allowlist.join('\n'),
    databaseAllowlist: target.database_allowlist.join('\n'),
    host: text(config.host),
    port: num(config.port, DEFAULT_PORTS[target.kind] ?? 1433),
    database: text(config.database),
    encrypt: config.encrypt !== false,
    trustServerCertificate: config.trustServerCertificate === true,
    baseUrl: text(config.baseUrl),
    pathAllowlist: asStrings(config.pathAllowlist).join('\n'),
    allowedMethods: asStrings(config.allowedMethods),
    allowedEgressCidrs: asStrings(config.allowedEgressCidrs).join('\n'),
    rateLimitPerSecond: num(config.rateLimitPerSecond, 10),
    rateLimitBurst: num(config.rateLimitBurst, 20),
  };
}

function TargetEditor({
  draft,
  onChange,
  onSave,
  onClose,
  pending,
}: {
  draft: Draft;
  onChange: (draft: Draft) => void;
  onSave: () => void;
  onClose: () => void;
  pending: boolean;
}): ReactElement {
  const set = (patch: Partial<Draft>): void => onChange({ ...draft, ...patch });
  const parsed = createTargetSchema.safeParse(draftToBody(draft));
  const isRest = draft.kind === 'rest';
  const isServer = draft.kind === 'mssql_server';
  const isEdit = draft.id !== undefined;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isEdit ? `Edit ${draft.name}` : 'Register a target'}
      description="Registering a target confers nothing on its own. A credential has to be bound to it, and a role has to be granted against it, before anyone can run anything."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} loading={pending} disabled={!parsed.success}>
            {isEdit ? 'Save changes' : 'Register'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-3">
          <TextField
            label="Key"
            containerClassName="w-56"
            className="font-mono text-xs"
            placeholder="sydawsprd-db02-ods"
            hint="Stable identifier used in approval rules and audit records."
            value={draft.key}
            onChange={(event) => set({ key: event.target.value })}
          />
          <TextField
            label="Name"
            containerClassName="min-w-64 flex-1"
            placeholder="SYDAWSPRD-DB02 · ODS"
            value={draft.name}
            onChange={(event) => set({ name: event.target.value })}
          />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <SelectField
            label="Kind"
            containerClassName="w-40"
            value={draft.kind}
            disabled={isEdit}
            /**
             * Immutable after creation, enforced by the server too. Changing
             * it would leave the bindings, allowlists and every historical
             * job pointing at something that is no longer the same system.
             */
            description={isEdit ? 'Fixed at registration' : KIND_DESCRIPTIONS[draft.kind]}
            options={TARGET_KINDS.map((kind) => ({ value: kind, label: kind }))}
            onChange={(event) =>
              set({
                kind: event.target.value,
                port: DEFAULT_PORTS[event.target.value] ?? draft.port,
              })
            }
          />
          <SelectField
            label="Environment"
            containerClassName="w-40"
            value={draft.environment}
            options={ENVIRONMENTS.map((env) => ({ value: env, label: env }))}
            onChange={(event) => set({ environment: event.target.value })}
          />
          <Checkbox
            label="Enabled"
            description="Disabled targets keep their history but accept no new work."
            checked={draft.enabled}
            onChange={(event) => set({ enabled: event.target.checked })}
          />
        </div>

        <TextAreaField
          label="Description"
          rows={2}
          placeholder="What this system is, and who owns it."
          value={draft.description}
          onChange={(event) => set({ description: event.target.value })}
        />

        {isRest ? (
          <RestFields draft={draft} set={set} />
        ) : (
          <DatabaseFields draft={draft} set={set} isServer={isServer} />
        )}

        <section className="flex flex-col gap-3 rounded-md border border-line p-2">
          <h3 className="text-xs font-medium text-subtle">Execution limits</h3>
          <div className="flex flex-wrap gap-3">
            <TextField
              label="Statement timeout"
              containerClassName="w-40"
              type="number"
              hint="seconds"
              value={draft.statementTimeoutSeconds}
              onChange={(event) => set({ statementTimeoutSeconds: Number(event.target.value) })}
            />
            <TextField
              label="Concurrency"
              containerClassName="w-32"
              type="number"
              hint="simultaneous executions"
              value={draft.maxConcurrency}
              onChange={(event) => set({ maxConcurrency: Number(event.target.value) })}
            />
            <TextField
              label="Interactive row cap"
              containerClassName="w-40"
              type="number"
              value={draft.interactiveRowCap}
              onChange={(event) => set({ interactiveRowCap: Number(event.target.value) })}
            />
            <TextField
              label="Batch row cap"
              containerClassName="w-40"
              type="number"
              value={draft.batchRowCap}
              onChange={(event) => set({ batchRowCap: Number(event.target.value) })}
            />
          </div>

          {!isRest ? (
            <Checkbox
              label="Allow multi-statement scripts"
              description="Several statements in one submission, every one of which must be a read. A write, DDL or procedure call anywhere in a script is still refused, whoever submits it."
              checked={draft.allowScripts}
              onChange={(event) => set({ allowScripts: event.target.checked })}
            />
          ) : null}

          {!isRest ? (
            <div className="flex flex-wrap gap-3">
              <TextAreaField
                label="Schema allowlist"
                containerClassName="min-w-64 flex-1"
                rows={3}
                className="font-mono text-xs"
                hint="One per line. Empty means everything the read account can already see."
                value={draft.schemaAllowlist}
                onChange={(event) => set({ schemaAllowlist: event.target.value })}
              />
              <TextAreaField
                label="Procedure allowlist"
                containerClassName="min-w-64 flex-1"
                rows={3}
                className="font-mono text-xs"
                hint={
                  isServer
                    ? 'One per line, each written database.schema.procedure. Empty means no procedure may be called at all.'
                    : 'One per line. Empty means no procedure may be called at all.'
                }
                value={draft.procAllowlist}
                onChange={(event) => set({ procAllowlist: event.target.value })}
              />
            </div>
          ) : null}

          {/*
            On a server, `dbo.usp_Recalc` names a different procedure in
            every database on it, so an unqualified entry would either
            allowlist sixteen procedures or none. The server refuses it and
            the reason is here rather than only in the validation message.
          */}
          {isServer && lines(draft.procAllowlist).length > 0 ? (
            <Alert tone="info">
              Each procedure here skips approval. On a whole server a two-part name is ambiguous —
              <code className="mx-1 font-mono text-xs">dbo.usp_Recalc</code> exists separately in
              every database — so each entry has to name its database.
            </Alert>
          ) : null}
        </section>

        {parsed.success ? null : (
          <Alert tone="info">
            <p className="mb-1 font-medium">Still needed before this can be saved</p>
            <ul className="list-disc pl-4">
              {parsed.error.issues.slice(0, 6).map((issue) => (
                <li key={`${issue.path.join('.')}:${issue.message}`}>
                  <code className="font-mono text-xs">{issue.path.join('.') || 'target'}</code>{' '}
                  {issue.message}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </div>
    </Modal>
  );
}

function DatabaseFields({
  draft,
  set,
  isServer,
}: {
  draft: Draft;
  set: (patch: Partial<Draft>) => void;
  isServer: boolean;
}): ReactElement {
  const allowlisted = lines(draft.databaseAllowlist);

  return (
    <section className="flex flex-col gap-3 rounded-md border border-line p-2">
      <h3 className="text-xs font-medium text-subtle">Connection</h3>

      <div className="flex flex-wrap gap-3">
        <TextField
          label="Host"
          containerClassName="min-w-64 flex-1"
          className="font-mono text-xs"
          placeholder="sydawsprd-db02.lauau.corp"
          hint="Resolved from inside the VPC, so it must be a name the task's resolver knows."
          value={draft.host}
          onChange={(event) => set({ host: event.target.value })}
        />
        <TextField
          label="Port"
          containerClassName="w-28"
          type="number"
          value={draft.port}
          onChange={(event) => set({ port: Number(event.target.value) })}
        />
        {/*
          No database field on a server target. The field is not merely
          unused there -- a value in it would look like a constraint while
          every submission picked its own database, so it is absent rather
          than disabled.
        */}
        {isServer ? null : (
          <TextField
            label="Database"
            containerClassName="w-48"
            className="font-mono text-xs"
            hint="One database per target."
            value={draft.database}
            onChange={(event) => set({ database: event.target.value })}
          />
        )}
      </div>

      {isServer ? (
        <>
          <TextAreaField
            label="Database allowlist"
            rows={3}
            className="font-mono text-xs"
            hint="One per line. Empty means every database on the server that the service account can open."
            value={draft.databaseAllowlist}
            onChange={(event) => set({ databaseAllowlist: event.target.value })}
          />

          {/*
            The breadth warning, and the reason this kind exists at all.
            An empty allowlist here is not the same mild thing an empty
            schema allowlist is: the schema allowlist narrows within one
            approved database, whereas this is the only thing standing
            between a grant on this target and every database the login can
            reach -- including ones nobody put through an approval.
          */}
          {allowlisted.length === 0 ? (
            <Alert tone="warning">
              <p className="mb-1 font-medium">
                This target reaches every database the service account can open
              </p>
              <p>
                Anyone granted it can browse and query all of them. Narrow it by listing the
                databases support actually needs — that list is what an access review reads, and it
                is the difference between a reviewable grant and an unbounded one. System databases
                are refused whatever is listed here.
              </p>
            </Alert>
          ) : (
            <p className="text-xs text-muted">
              Limited to {allowlisted.length} database{allowlisted.length === 1 ? '' : 's'}:{' '}
              {allowlisted.join(', ')}. Anything else is refused, whoever asks.
            </p>
          )}
        </>
      ) : null}

      <div className="flex flex-wrap items-start gap-6">
        <Checkbox
          label="Encrypt the connection"
          checked={draft.encrypt}
          onChange={(event) => set({ encrypt: event.target.checked })}
        />
        <Checkbox
          label="Trust the server certificate"
          description="Skips verification. Only for a server whose certificate this tool cannot chain."
          checked={draft.trustServerCertificate}
          onChange={(event) => set({ trustServerCertificate: event.target.checked })}
        />
      </div>

      {draft.trustServerCertificate ? (
        // Not a blocker: an internal SQL Server with a self-signed
        // certificate is a real situation. But it is recorded as a
        // `target.weakened` event, and saying so here is cheaper than
        // someone learning it from an alert.
        <Alert tone="warning">
          The connection will be encrypted but unauthenticated — that stops passive capture and
          nothing else. Saving this raises a control-weakening audit event.
        </Alert>
      ) : null}
    </section>
  );
}

function RestFields({
  draft,
  set,
}: {
  draft: Draft;
  set: (patch: Partial<Draft>) => void;
}): ReactElement {
  const toggleMethod = (method: string): void =>
    set({
      allowedMethods: draft.allowedMethods.includes(method)
        ? draft.allowedMethods.filter((m) => m !== method)
        : [...draft.allowedMethods, method],
    });

  return (
    <section className="flex flex-col gap-3 rounded-md border border-line p-2">
      <h3 className="text-xs font-medium text-subtle">Endpoint</h3>

      <TextField
        label="Base URL"
        className="font-mono text-xs"
        placeholder="https://api.example.edu.au"
        hint="https only. Callers supply a path relative to this and can never supply a host."
        value={draft.baseUrl}
        onChange={(event) => set({ baseUrl: event.target.value })}
      />

      <div>
        <p className="mb-1 text-xs font-medium text-subtle">Methods</p>
        <div className="flex flex-wrap gap-4">
          {HTTP_METHODS.map((method) => (
            <Checkbox
              key={method}
              label={method}
              checked={draft.allowedMethods.includes(method)}
              onChange={() => toggleMethod(method)}
            />
          ))}
        </div>
      </div>

      <TextAreaField
        label="Path allowlist"
        rows={3}
        className="font-mono text-xs"
        hint="One per line, each starting with a slash. At least one is required."
        value={draft.pathAllowlist}
        onChange={(event) => set({ pathAllowlist: event.target.value })}
      />

      <TextAreaField
        label="Egress ranges"
        rows={2}
        className="font-mono text-xs"
        hint="One CIDR per line. Empty means public addresses only, which is the safe default."
        value={draft.allowedEgressCidrs}
        onChange={(event) => set({ allowedEgressCidrs: event.target.value })}
      />

      {lines(draft.allowedEgressCidrs).length > 0 ? (
        <Alert tone="warning">
          Listing a private range is how an internal endpoint becomes reachable. This is the change
          an SSRF review starts from.
        </Alert>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <TextField
          label="Rate limit"
          containerClassName="w-32"
          type="number"
          hint="per second"
          value={draft.rateLimitPerSecond}
          onChange={(event) => set({ rateLimitPerSecond: Number(event.target.value) })}
        />
        <TextField
          label="Burst"
          containerClassName="w-32"
          type="number"
          value={draft.rateLimitBurst}
          onChange={(event) => set({ rateLimitBurst: Number(event.target.value) })}
        />
      </div>
    </section>
  );
}

/**
 * What the target points at, in one line.
 *
 * Shown because an administrator configuring a target needs to see it -- but
 * this page is behind `target.manage`, so the internal topology goes to the
 * small group whose job it is rather than to every authenticated user. The
 * console's own target list omits all of it.
 */
export function describeAddress(target: AdminTarget): string {
  if (target.kind === 'rest') return String(target.config.baseUrl ?? '—');

  const host = String(target.config.host ?? '?');
  const port = String(target.config.port ?? '?');

  /**
   * A server target has no database in its config, so printing
   * `host:port/undefined` -- which the `?? '?'` fallback would have made
   * `host:port/?` -- would read as a misconfigured target rather than a
   * correctly configured one of a different kind.
   */
  if (target.kind === 'mssql_server') return `${host}:${port} (whole server)`;

  return `${host}:${port}/${String(target.config.database ?? '?')}`;
}

function asStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}
